import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/utils/analytics";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.76, 0, 0.24, 1];

const TIERS = [
  {
    name: "Independent",
    price: "$5",
    period: "/MO",
    desc: "Fast personal deal prep with the core engines and predictable monthly capacity.",
    lookup: "independent_monthly",
    cta: "Start free trial",
    access: "Core access",
    proof: "20 conversions included",
    features: [
      [true, "7-day free trial"],
      [true, "20 conversions per month"],
      [true, "15MB max file size"],
      [true, "All 16 core engines"],
      [true, "Standard email support"],
      [false, "Batch processing engine"],
    ],
    featured: false,
  },
  {
    name: "Professional",
    price: "$50",
    period: "/MO",
    desc: "Unlimited document throughput for analysts and deal teams under deadline.",
    lookup: "professional_monthly",
    cta: "Start free trial",
    access: "Full access",
    proof: "Unlimited conversions",
    features: [
      [true, "Unlimited conversions"],
      [true, "50MB max file size"],
      [true, "Batch processing engine"],
      [true, "Permanent redaction + AI OCR"],
      [true, "Priority support"],
    ],
    featured: true,
  },
  {
    name: "Enterprise",
    price: "CUSTOM",
    period: "",
    desc: "Firm-wide automation for high-volume diligence, integrations and governance.",
    lookup: null,
    cta: "Talk to sales",
    access: "Firm-wide access",
    proof: "Custom volume + integrations",
    features: [
      [true, "Everything in Professional"],
      [true, "Data-room + SSO integrations"],
      [true, "Dedicated account manager"],
      [true, "Custom retention policy"],
      [true, "100k+ file batching"],
    ],
    featured: false,
  },
];

export const Pricing = ({ indexLabel = "08 — Pricing" }) => {
  const [loading, setLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const sectionRef = useRef(null);
  const pricingViewTracked = useRef(false);
  const { user } = useAuth();
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);
  const [enterpriseStatus, setEnterpriseStatus] = useState("idle");
  const [enterpriseError, setEnterpriseError] = useState(null);
  const [enterpriseForm, setEnterpriseForm] = useState({ name: "", email: "", company: "", volume: "", message: "" });

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !pricingViewTracked.current) {
        pricingViewTracked.current = true;
        trackEvent("pricing_view", { source: "landing" });
      }
    }, { threshold: 0.25 });
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const checkout = async (tier) => {
    if (!tier.lookup) {
      trackEvent("cta_click", { source: "pricing_enterprise", plan: "enterprise" });
      setEnterpriseOpen(true);
      setEnterpriseStatus("idle");
      setEnterpriseError(null);
      return;
    }
    setLoading(tier.lookup);
    setCheckoutError(null);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: tier.lookup,
        origin_url: window.location.origin,
        ...(user && user.email ? { email: user.email } : {}),
      });
      trackEvent("checkout_start", { plan: tier.lookup, source: "pricing", session_id: res.data.session_id });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setLoading(null);
      setCheckoutError("Checkout could not start. Try again — if it continues, contact hello@doclyx.com.");
    }
  };

  const submitEnterprise = async (e) => {
    e.preventDefault();
    if (enterpriseStatus === "sending") return;
    if (!enterpriseForm.volume) {
      setEnterpriseStatus("error");
      setEnterpriseError("Select your monthly file volume.");
      return;
    }
    setEnterpriseStatus("sending");
    setEnterpriseError(null);
    try {
      await axios.post(`${API}/enterprise/lead`, enterpriseForm);
      setEnterpriseStatus("sent");
      setEnterpriseForm({ name: "", email: "", company: "", volume: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : "Check the highlighted fields — name and company need at least 2 characters, email must be valid, and workflow goals need at least 10 characters.";
      setEnterpriseStatus("error");
      setEnterpriseError(message);
    }
  };


  return (
    <section ref={sectionRef} id="pricing" className="py-24 md:py-32 lg:py-40 bg-white border-y border-hairline scroll-mt-24" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-10 md:mb-12">
          <motion.span
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ duration: 1.2, ease: EASE }}
            className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5"
          >
            {indexLabel}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 24, filter: "blur(8px)" }} whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }} viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease: EASE }}
            className="font-medium tracking-tight leading-tight text-3xl md:text-4xl lg:text-5xl text-ink max-w-3xl"
          >
            Choose the workspace that matches your deal flow.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 24, filter: "blur(8px)" }} whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }} viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.1 }}
            className="text-base text-fog leading-relaxed max-w-2xl mt-6"
          >
            Every plan unlocks the private workspace. Upgrade only when your document volume,
            batch needs, or governance requirements grow.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-10 max-w-6xl mx-auto"
          data-testid="plan-value-strip"
        >
          {TIERS.map((t) => (
            <div key={t.name} className="border border-hairline rounded-sm bg-page px-5 py-4" data-testid={`value-${t.name.toLowerCase()}`}>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mist block mb-1.5">{t.name}</span>
              <span className="text-sm font-medium text-ink">{t.proof}</span>
            </div>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch max-w-6xl mx-auto" data-testid="pricing-grid">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.1 }}
              className={`relative rounded-sm p-10 flex flex-col bg-white ${
                t.featured
                  ? "border border-edge border-t-2 border-t-azure shadow-[0_20px_40px_-20px_rgba(0,0,0,0.08)]"
                  : "border border-hairline"
              }`}
              data-testid={`pricing-tier-${i + 1}`}
            >
              {t.featured && (
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-azure mb-6 block" data-testid="pricing-popular-badge">
                  Recommended
                </span>
              )}
              {!t.featured && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-6 block">0{i + 1}</span>}
              <h3 className="text-xl font-medium tracking-tight mb-2 text-ink">{t.name}</h3>
              <span className={`inline-flex w-fit font-mono text-[10px] uppercase tracking-[0.18em] rounded-full border px-3 py-1 mb-4 ${t.featured ? "text-azure border-azure/30" : "text-mist border-hairline"}`} data-testid={`access-${t.name.toLowerCase()}`}>
                {t.access}
              </span>
              <p className="text-sm text-fog leading-relaxed mb-8">{t.desc}</p>
              <div className="flex items-baseline gap-1.5 mb-10">
                <span className="font-display font-medium tracking-tight text-5xl text-ink">{t.price}</span>
                {t.period && <span className="font-mono text-xs text-mist">{t.period.toLowerCase()}</span>}
              </div>
              <ul className="space-y-4 mb-10 flex-1">
                {t.features.map(([on, label]) => (
                  <li key={label} className={`flex items-center gap-3 text-sm ${on ? "text-ink" : "text-mist"}`}>
                    {on
                      ? <Check className={`w-4 h-4 shrink-0 ${t.featured ? "text-azure" : "text-ink"}`} strokeWidth={1.5} />
                      : <X className="w-4 h-4 shrink-0" strokeWidth={1.5} />}
                    {label}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => checkout(t)}
                disabled={!!t.lookup && loading === t.lookup}
                data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                className={`group w-full h-11 rounded-full text-sm font-medium flex items-center justify-center gap-2 transition-colors duration-300 ${
                  t.featured
                    ? "v4-btn-primary"
                    : "border border-edge text-ink hover:border-ink"
                }`}
              >
                {t.lookup && loading === t.lookup ? (
                  <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Redirecting…</>
                ) : (
                  <>{t.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" strokeWidth={1.5} /></>
                )}
              </button>
            </motion.div>
          ))}
        </div>
        {checkoutError && (
          <p className="font-mono text-xs text-[#FF3333] text-center mt-6" data-testid="checkout-error">{checkoutError}</p>
        )}
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-mist text-center mt-8" data-testid="pricing-note">
          Every plan starts with a free trial · Trials require plan selection and payment details · Cancel anytime
        </p>
      </div>

      <AnimatePresence>
        {enterpriseOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            data-testid="enterprise-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="w-full max-w-xl bg-white border border-edge rounded-sm shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-5 border-b border-hairline">
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-bronze block mb-1">Enterprise enquiry</span>
                  <h3 className="font-display text-2xl text-ink">Design your document pipeline.</h3>
                </div>
                <button type="button" onClick={() => setEnterpriseOpen(false)} data-testid="enterprise-close"
                  className="text-mist hover:text-ink transition-colors duration-200">
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              {enterpriseStatus === "sent" ? (
                <div className="p-8 text-center" data-testid="enterprise-success">
                  <h4 className="font-display text-2xl text-ink mb-3">Request received.</h4>
                  <p className="text-sm text-fog leading-relaxed mb-7">Your company details and volume are on their way to the Doclyx founder inbox.</p>
                  <button type="button" onClick={() => setEnterpriseOpen(false)} data-testid="enterprise-done"
                    className="v4-btn-primary h-10 px-6 rounded-full text-sm font-semibold">Done</button>
                </div>
              ) : (
                <form onSubmit={submitEnterprise} className="p-6 space-y-4" data-testid="enterprise-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-mist block mb-2">Full name</span>
                      <input required minLength={2} value={enterpriseForm.name} onChange={(e) => setEnterpriseForm({ ...enterpriseForm, name: e.target.value })}
                        placeholder="Full name" data-testid="enterprise-name"
                        className="h-11 w-full rounded-sm bg-page border border-hairline px-4 text-sm text-ink placeholder:text-mist focus:outline-none focus:border-ink" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-mist block mb-2">Work email</span>
                      <input required type="email" value={enterpriseForm.email} onChange={(e) => setEnterpriseForm({ ...enterpriseForm, email: e.target.value })}
                        placeholder="Work email" data-testid="enterprise-email"
                        className="h-11 w-full rounded-sm bg-page border border-hairline px-4 text-sm text-ink placeholder:text-mist focus:outline-none focus:border-ink" />
                    </label>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-mist block mb-2">Company</span>
                      <input required minLength={2} value={enterpriseForm.company} onChange={(e) => setEnterpriseForm({ ...enterpriseForm, company: e.target.value })}
                        placeholder="Company" data-testid="enterprise-company"
                        className="h-11 w-full rounded-sm bg-page border border-hairline px-4 text-sm text-ink placeholder:text-mist focus:outline-none focus:border-ink" />
                    </label>
                    <label className="block">
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-mist block mb-2">Monthly file volume</span>
                      <Select value={enterpriseForm.volume} onValueChange={(value) => setEnterpriseForm({ ...enterpriseForm, volume: value })}>
                        <SelectTrigger data-testid="enterprise-volume" className="h-11 w-full bg-page border-hairline text-ink focus:border-ink focus:ring-0 rounded-sm">
                          <SelectValue placeholder="Select volume" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-hairline text-ink">
                          <SelectItem value="Under 1,000 files" className="focus:bg-page focus:text-ink">Under 1,000 files</SelectItem>
                          <SelectItem value="1,000–10,000 files" className="focus:bg-page focus:text-ink">1,000–10,000 files</SelectItem>
                          <SelectItem value="10,000–100,000 files" className="focus:bg-page focus:text-ink">10,000–100,000 files</SelectItem>
                          <SelectItem value="100,000+ files" className="focus:bg-page focus:text-ink">100,000+ files</SelectItem>
                        </SelectContent>
                      </Select>
                    </label>
                  </div>
                  <label className="block">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-mist block mb-2">Workflow goals</span>
                    <textarea required minLength={10} value={enterpriseForm.message} onChange={(e) => setEnterpriseForm({ ...enterpriseForm, message: e.target.value })}
                      placeholder="What should the pipeline automate — redaction, data-room export, integrations, or governance?" rows={5}
                      data-testid="enterprise-message"
                      className="w-full rounded-sm bg-page border border-hairline px-4 py-3 text-sm text-ink placeholder:text-mist focus:outline-none focus:border-ink resize-none" />
                  </label>
                  {enterpriseError && <p className="font-mono text-xs text-[#FF3333]" data-testid="enterprise-error">{enterpriseError}</p>}
                  <button type="submit" disabled={enterpriseStatus === "sending"} data-testid="enterprise-submit"
                    className="w-full h-11 rounded-full v4-btn-primary text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                    {enterpriseStatus === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Sending…</> : <>Send enterprise enquiry <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
